import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "./motion-primitives";
import { useLang, Rich } from "@/i18n/LanguageContext";

const ROW_A = [2, 11, 5, 16, 20, 40, 26, 1, 8, 28, 25, 13].map((n) => `/images/osrelis-${n}.jpg`);
const ROW_B = [10, 21, 6, 27, 41, 17, 9, 39, 12, 29, 35, 3].map((n) => `/images/osrelis-${n}.jpg`);
const ALL = [...ROW_A, ...ROW_B];

const Row = ({ images, reverse = false, duration = "48s", onOpen }) => (
  <div className="group overflow-hidden">
    <div
      className="marquee-track animate-marquee group-hover:[animation-play-state:paused]"
      style={{ animationDuration: duration, animationDirection: reverse ? "reverse" : "normal" }}
    >
      {[...images, ...images].map((src, i) => (
        <button
          key={i}
          type="button"
          data-cursor
          onClick={() => onOpen(src)}
          aria-label="Open image"
          className="mx-2 h-[170px] w-[260px] shrink-0 overflow-hidden md:mx-3 md:h-[230px] md:w-[360px]"
        >
          <img
            src={src}
            alt="OSRELIS field operation"
            loading="lazy"
            className="h-full w-full object-cover grayscale transition-[filter,transform] duration-[900ms] hover:scale-105 hover:grayscale-0"
          />
        </button>
      ))}
    </div>
  </div>
);

export const Gallery = () => {
  const { c } = useLang();
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const next = useCallback(() => setIndex((i) => (i + 1) % ALL.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + ALL.length) % ALL.length), []);
  const openSrc = (src) => setIndex(ALL.indexOf(src));

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close, next, prev]);

  return (
    <section id="gallery" data-testid="section-gallery" className="overflow-hidden bg-deep py-20 md:py-28">
      <Reveal className="mb-12 px-6 md:mb-16 md:px-[8vw]">
        <p className="mb-4 font-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-sage">{c.gallery.kicker}</p>
        <h2 className="font-serif text-[clamp(40px,5vw,76px)] font-normal leading-[0.98] tracking-[-0.02em] text-paper">
          <Rich text={c.gallery.title} />
        </h2>
      </Reveal>
      <div className="flex flex-col gap-3 md:gap-5">
        <Row images={ROW_A} duration="52s" onOpen={openSrc} />
        <Row images={ROW_B} reverse duration="60s" onOpen={openSrc} />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[130] flex items-center justify-center bg-black/92 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
          >
            <button
              type="button"
              data-testid="lightbox-close"
              data-cursor
              onClick={close}
              aria-label="Close"
              className="absolute right-5 top-5 z-[2] flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-200 hover:border-teal hover:text-teal md:right-8 md:top-8"
            >
              <X size={20} />
            </button>

            <button
              type="button"
              data-testid="lightbox-prev"
              data-cursor
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous"
              className="absolute left-4 z-[2] flex h-12 w-12 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-200 hover:border-teal hover:text-teal md:left-8"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              data-testid="lightbox-next"
              data-cursor
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next"
              className="absolute right-4 z-[2] flex h-12 w-12 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-200 hover:border-teal hover:text-teal md:right-8"
            >
              <ChevronRight size={22} />
            </button>

            <motion.img
              key={index}
              src={ALL[index]}
              alt="OSRELIS field operation"
              data-testid="lightbox-image"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[84vh] max-w-[90vw] object-contain shadow-2xl"
            />

            <span
              data-testid="lightbox-counter"
              className="absolute bottom-6 left-1/2 -translate-x-1/2 font-sans text-[11px] tracking-[0.2em] text-white/70"
            >
              {String(index + 1).padStart(2, "0")} / {String(ALL.length).padStart(2, "0")}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
